/*
 * Beyond the Fridge — Made in my town generator (screen 13).
 *
 * Ported from the design's Component logic. The board face is the supplied master artwork,
 * made_in_red_aw_lg.svg: its 2184.59 × 2905.21 artboard is the 75 × 100 cm portrait board
 * exactly. The crest is the master's own 22 outlined paths, reproduced at master coordinates
 * with only its fill set; the lockup (MADE / IN / the town) sits on the master's centre line,
 * baselines and leading. Only the town is variable: it takes the master size where it fits,
 * shrinks where it does not, and wraps to two lines before it gets too small. The master's
 * own type size is solved from the reference word (CHELSEA measures 1618.86 units), so it
 * self-calibrates to whichever face renders. Gill Sans is named first and falls back.
 *
 * The board is shown on the Create Your Own three-layer render: the face under the blank
 * photographed product plate on multiply (gloss, shading and edge roll come from the
 * photograph), with the screw caps on top keeping their own colour. Plate rectangles and the
 * 35 mm corner radius are the Create Your Own numbers.
 *
 * Elements:
 *   <btf-town-board>    one rendered board (examples, swatches, the live preview)
 *   <btf-town-preview>  the product-page controller: wires the fields, Horizon's variant
 *                       picker (Size), the format / caps toggles, the colourway links and the
 *                       Product view / On the wall tabs to the board
 */

// Board sizes in cm, [width, height]
const CM = {
  Landscape: { Small: [55, 40], Medium: [75, 55], Large: [100, 75] },
  Portrait: { Small: [40, 55], Medium: [55, 75], Large: [75, 100] },
};

// The board rectangle inside each production plate, [left%, top%, width%, height%], from Create Your Own
const PROD = {
  Landscape: { ar: '1000 / 730', arNum: 1000 / 730, Large: [4.3, 2.9, 90.7, 93.2], Medium: [15.8, 15.6, 68.3, 68.6], Small: [25.0, 25.1, 49.9, 49.6] },
  Portrait: { ar: '1 / 1', arNum: 1, Large: [16.0, 4.3, 68.0, 90.6], Medium: [25.0, 15.8, 49.7, 68.3], Small: [31.6, 21.0, 36.2, 49.9] },
};

// The same for the room composites (all square)
const ROOM = {
  Landscape: { Large: [27.0, 9.6, 47.5, 35.6], Medium: [32.5, 18.9, 35.3, 25.7], Small: [36.5, 20.2, 31.2, 22.6] },
  Portrait: { Large: [33.4, 4.7, 35.2, 47.0], Medium: [36.8, 14.3, 25.7, 35.3], Small: [40.5, 16.0, 22.6, 31.2] },
};

const PLATE = { Small: 'sm', Medium: 'md', Large: 'lg' };

// Gill Sans is the product's face. It is not licensed into this project, so the stack names it
// first and falls back — correct on a machine that has it, an approximation elsewhere.
export const FACE = '"Gill Sans", "Gill Sans MT", "Gill Sans Nova", Calibri, "Trebuchet MS", sans-serif';

// The lettering and crest ink: the pale cream of the original print rather than a flat white.
// Pale grounds take the dark ink instead, where this would vanish.
const INK = '#fff6d2';
const DARK_INK = '#3a332a';

// Colourways. Brown, green, blue, grey, black, red, cream and teal are the design's; pink is
// sampled from the live product photograph and white is the plain white board. A product's
// own swatch colour (data-hex) overrides these.
export const COLOURS = {
  brown: '#6b4a33', green: '#3f4f3c', blue: '#2f4858', grey: '#54534f', black: '#232321',
  red: '#b8131b', cream: '#ddd5c4', teal: '#1d5b5b', pink: '#bc3e90', white: '#ffffff',
};
const PALE = /^(cream|white|ivory|yellow)$/;

// The master: artboard, the crest's bounding box (getBBox, not parsed), the first baseline, the
// leading, the reference word and the corner radius in mm.
const M = { w: 2184.59, h: 2905.21, cx: 1092.3, crest: [840.4, 285.2, 505.9, 467.1], base1: 1334.8159, lead: 480, ref: 'CHELSEA', refW: 1618.86, radius: 32 };

// The crest: the master's 22 outlined paths, verbatim
const CREST = `<circle cx="1044.97" cy="392.34" r="12.87"></circle><path d="M1024.39,400.86c0,6.76-5.47,12.23-12.23,12.23s-12.23-5.48-12.23-12.23,5.48-12.23,12.23-12.23,12.23,5.48,12.23,12.23Z"></path><path d="M992.8,412.9c0,6.28-5.09,11.38-11.37,11.38s-11.38-5.09-11.38-11.38,5.09-11.38,11.38-11.38,11.37,5.09,11.37,11.38Z"></path><path d="M964.9,426.4c0,5.78-4.69,10.47-10.46,10.47s-10.47-4.69-10.47-10.47,4.69-10.46,10.47-10.46,10.46,4.68,10.46,10.46Z"></path><path d="M937.06,443.84c0,5.32-4.31,9.63-9.63,9.63s-9.63-4.31-9.63-9.63,4.31-9.63,9.63-9.63,9.63,4.31,9.63,9.63Z"></path><path d="M912.41,461.91c0,4.89-3.96,8.86-8.86,8.86s-8.86-3.97-8.86-8.86,3.96-8.86,8.86-8.86,8.86,3.96,8.86,8.86Z"></path><path d="M892.92,480.81c0,4.45-3.61,8.06-8.06,8.06s-8.06-3.61-8.06-8.06,3.61-8.06,8.06-8.06,8.06,3.61,8.06,8.06Z"></path><path d="M898.38,502.67h0s0,0,0,0h0ZM889.9,493.55c.18-.16,20.34-18.92,51.03-38.88h0c30.72-19.93,71.92-41.18,114.79-45.83h0l3.92-.42,2.06,3.36c.3.53,15.76,25.7,17.53,62.77h0l-12.45.58c-1.19-25.87-9.93-45.74-13.83-53.41h0c-35.8,4.79-71.54,22.19-99.53,39.75h0c-29.08,18.21-49.84,36.48-54.19,40.42h0c-.58.52-.84.77-.85.77h0l-8.48-9.12h0Z"></path><circle cx="1141.67" cy="392.34" r="12.87"></circle><path d="M1162.25,400.86c0,6.76,5.48,12.23,12.23,12.23s12.23-5.48,12.23-12.23-5.48-12.23-12.23-12.23-12.23,5.48-12.23,12.23Z"></path><path d="M1193.84,412.9c0,6.28,5.09,11.38,11.37,11.38s11.37-5.09,11.37-11.38-5.09-11.38-11.37-11.38-11.37,5.09-11.37,11.38Z"></path><path d="M1221.75,426.4c0,5.78,4.69,10.47,10.46,10.47s10.46-4.69,10.46-10.47-4.69-10.46-10.46-10.46-10.46,4.68-10.46,10.46Z"></path><path d="M1249.58,443.84c0,5.32,4.31,9.63,9.63,9.63s9.62-4.31,9.62-9.63-4.31-9.63-9.62-9.63-9.63,4.31-9.63,9.63Z"></path><path d="M1274.23,461.91c0,4.89,3.96,8.86,8.85,8.86s8.86-3.97,8.86-8.86-3.96-8.86-8.86-8.86-8.85,3.96-8.85,8.86Z"></path><path d="M1293.72,480.81c0,4.45,3.61,8.06,8.06,8.06s8.06-3.61,8.06-8.06-3.61-8.06-8.06-8.06-8.06,3.61-8.06,8.06Z"></path><path d="M1288.37,502.67h0s0,0,0,0h0ZM1296.86,493.55l-8.49,9.12h0s-.27-.25-.85-.77h0c-4.35-3.93-25.11-22.21-54.19-40.42h0c-28-17.56-63.74-34.96-99.53-39.75h0c-3.91,7.67-12.64,27.54-13.83,53.41l-12.45-.58h0c1.77-37.07,17.23-62.24,17.53-62.77l2.06-3.36,3.91.42h0c42.87,4.65,84.08,25.9,114.8,45.83h0c30.7,19.95,50.85,38.71,51.04,38.88h0Z"></path><path d="M1099.76,346.88v-16.37l20.77,9.16v-36.96l-21.09,11.7,13.62-29.22h-39.04l13.76,29.53-21.65-12.01v36.96l20.77-9.16v16.37c-12.1,2.9-21.1,13.78-21.1,26.78,0,15.21,12.33,27.54,27.54,27.54s27.54-12.33,27.54-27.54c0-12.99-9-23.88-21.11-26.78ZM1086.97,355.05v14.99l-12.22-7.49,12.22-7.5ZM1093.95,394.21c-8.49,0-15.99-5.23-15.99-7.06s6.88-3.32,15.37-3.32,15.37,1.49,15.37,3.32-6.26,7.06-14.75,7.06ZM1099.67,370.04v-14.99l12.22,7.5-12.22,7.49Z"></path><path d="M1104.7,419.48c0,6.28-5.09,11.38-11.37,11.38s-11.37-5.09-11.37-11.38,5.09-11.38,11.37-11.38,11.37,5.09,11.37,11.38Z"></path><path d="M1102.95,446.61c0,5.32-4.31,9.63-9.63,9.63s-9.63-4.31-9.63-9.63,4.31-9.63,9.63-9.63,9.63,4.31,9.63,9.63Z"></path><path d="M1101.38,470.7c0,4.45-3.61,8.06-8.06,8.06s-8.06-3.61-8.06-8.06,3.61-8.06,8.06-8.06,8.06,3.61,8.06,8.06Z"></path><path d="M1346.29,516.88l-42.08-10.76,16.26,36.58-24.15-17.69-13.15,63.84,30.37-18.17s-14.79,52.43-53.56,51.65c-47.58-.96-56.91-58.82-22.24-62.89,0,0,22.24-2.39,20.32,38.26,0,0,22.48-7.89,22.48-38.5,0-36.82-33.95-37.78-33.95-37.78,0,0-14.83-.72-20.8,4.79,0,0,18.41-35.63-5.26-68.87,0,0-33.24,19.37-26.78,65.04,0,0-9.8-11-26.06-11s-26.3,19.37-26.3,33,15.06,29.17,21.04,31.56c0,0-3.66-25.79,14.58-26.3,16.98-.48,19.13,23.43,19.13,23.43,0,0,1.68,34.17-44.96,34.43-41.84.24-45.91-48.3-45.91-48.3l25.35,13.63v-63l-25.83,18.77,12.44-44.47h-47.8l12.43,44.47-25.83-18.77v63l25.35-13.63s-4.06,48.54-45.91,48.3c-46.63-.27-44.95-34.43-44.95-34.43,0,0,2.15-23.91,19.13-23.43,18.24.51,14.58,26.3,14.58,26.3,5.98-2.39,21.04-17.93,21.04-31.56s-10.04-33-26.3-33-26.06,11-26.06,11c6.46-45.67-26.78-65.04-26.78-65.04-23.67,33.24-5.26,68.87-5.26,68.87-5.98-5.5-20.8-4.79-20.8-4.79,0,0-33.96.96-33.96,37.78,0,30.61,22.48,38.5,22.48,38.5-1.91-40.65,20.32-38.26,20.32-38.26,34.67,4.07,25.34,61.93-22.24,62.89-38.76.78-53.56-51.65-53.56-51.65l30.37,18.17-13.15-63.84-24.15,17.69,16.26-36.58-42.08,10.76s11.94,24.88,8.37,33.71c-5.5,13.63-2.58,19.96-.48,25.11,2.63,6.46,11.48,12.43,12.43,14.35.96,1.91,8.13,25.58,10.76,43.28,2.63,17.7-4.51,45.32-4.51,45.32l34.88,32.63c0-3.21,80.34-33.95,191.29-31.8h.45c110.95-2.15,191.29,28.59,191.29,31.8l34.88-32.63s-7.14-27.63-4.51-45.32c2.63-17.69,9.8-41.37,10.76-43.28.96-1.91,9.8-7.89,12.43-14.35,2.1-5.15,5.03-11.48-.48-25.11-3.56-8.84,8.37-33.71,8.37-33.71ZM860.32,576.39l-8.4-6.97,3.92-10.19,7.6,7.18-3.12,9.97ZM891.39,684.37l-12.08-11.07,4.99-15.03,11.07,10.6-3.98,15.5ZM920.26,648.89c0-3.78,3.07-6.85,6.85-6.85s6.85,3.07,6.85,6.85-3.07,6.85-6.85,6.85-6.85-3.07-6.85-6.85ZM931.42,683.11c-3.79,0-6.85-3.07-6.85-6.85s3.06-6.85,6.85-6.85,6.85,3.07,6.85,6.85-3.07,6.85-6.85,6.85ZM990.41,666.85c-16.02,1.47-29.87-6.78-30.94-18.43-1.07-11.65,11.05-22.29,27.07-23.76,16.02-1.47,29.87,6.79,30.94,18.44,1.07,11.65-11.05,22.28-27.07,23.75ZM1034.8,629.03c.2-3.78,3.42-6.68,7.2-6.48,3.78.2,6.68,3.42,6.49,7.2-.2,3.78-3.42,6.68-7.2,6.49-3.78-.2-6.68-3.42-6.48-7.2ZM1051.36,657.29c-.2,3.78-3.42,6.68-7.2,6.48-3.78-.2-6.68-3.42-6.48-7.2.2-3.78,3.42-6.68,7.2-6.49,3.78.2,6.68,3.42,6.49,7.2ZM1093.32,537.98c2.75,0,4.98,2.23,4.98,4.98s-2.23,4.99-4.98,4.99-4.98-2.23-4.98-4.99,2.23-4.98,4.98-4.98ZM1092.89,657.79l-11.77-19.02,11.77-19.02,12.63,19.02-12.63,19.02ZM1142.48,663.78c-3.78.2-7-2.71-7.2-6.48-.19-3.78,2.71-7,6.49-7.2,3.78-.2,7,2.71,7.2,6.49.19,3.78-2.71,7-6.49,7.2ZM1145.36,636.23c-3.77.2-7-2.71-7.2-6.49-.2-3.78,2.71-7,6.48-7.2,3.78-.2,7,2.71,7.2,6.48.2,3.78-2.71,7-6.48,7.2ZM1227.18,648.42c-1.07,11.65-14.92,19.9-30.94,18.43-16.02-1.47-28.14-12.11-27.07-23.75,1.07-11.65,14.93-19.91,30.94-18.44,16.02,1.47,28.14,12.11,27.07,23.76ZM1255.22,683.11c-3.79,0-6.85-3.07-6.85-6.85s3.07-6.85,6.85-6.85,6.85,3.07,6.85,6.85-3.07,6.85-6.85,6.85ZM1259.53,655.75c-3.78,0-6.85-3.07-6.85-6.85s3.07-6.85,6.85-6.85,6.85,3.07,6.85,6.85-3.07,6.85-6.85,6.85ZM1295.26,684.37l-3.98-15.5,11.08-10.6,4.99,15.03-12.08,11.07ZM1326.32,576.39l-3.12-9.97,7.59-7.18,3.92,10.19-8.4,6.97Z"></path><path d="M1274.23,721.28c0,17.12-80.95,31.01-180.8,31.01s-180.8-13.88-180.8-31.01,80.95-31.01,180.8-31.01,180.8,13.88,180.8,31.01Z"></path>`;

export function colourKey(value) {
  const v = String(value || '').toLowerCase();
  if (/grey|gray|storm/.test(v)) return 'grey';
  if (/rose|pink/.test(v)) return 'pink';
  return v.trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export function sizeKey(value) {
  const v = String(value || '');
  if (/small/i.test(v)) return 'Small';
  if (/medium/i.test(v)) return 'Medium';
  if (/large/i.test(v)) return 'Large';
  const m = v.match(/(\d+(?:\.\d+)?)\s*(?:cm)?[^\d]+?(\d+(?:\.\d+)?)/);
  if (m) {
    const big = Math.max(parseFloat(m[1]), parseFloat(m[2]));
    return big <= 60 ? 'Small' : big <= 80 ? 'Medium' : 'Large';
  }
  return 'Large';
}

export const orientKey = (v) => (/land/i.test(String(v || '')) ? 'Landscape' : 'Portrait');

// ---- measuring: a hidden span carrying the identical face, weight and tracking ------------
let span = null;
const cache = new Map();
export function measure(str) {
  if (!str) return 0;
  if (cache.has(str)) return cache.get(str);
  if (span && !span.isConnected) span = null;
  if (!span) {
    span = document.createElement('span');
    span.setAttribute('style', 'position:absolute;left:-9999px;top:0;visibility:hidden;white-space:pre;font-family:' + FACE + ';font-weight:600;font-size:100px;letter-spacing:1px;');
    document.body.appendChild(span);
  }
  span.textContent = str;
  const w = span.offsetWidth;
  if (w > 0) cache.set(str, w);
  return w;
}

// the master's own type size, solved from the reference word
function masterFs() {
  const at = measure(M.ref);
  return at ? (M.refW * 100) / at : 364;
}

// tokens break on spaces AND after hyphens, so STOKE-ON-TRENT can wrap
function tokens(str) {
  const out = [];
  String(str || '').trim().split(/\s+/).filter(Boolean).forEach((w) => {
    const parts = w.split('-');
    parts.forEach((p, i) => { if (p || i < parts.length - 1) out.push(i < parts.length - 1 ? p + '-' : p); });
  });
  return out;
}
const join = (toks) => toks.reduce((acc, tk, i) => (i === 0 ? tk : acc + (/-$/.test(acc) ? '' : ' ') + tk), '');

/**
 * Lays the lockup out on a board of cm[0] × cm[1], in millimetres. Nothing is stretched: the
 * crest is only ever translated and uniformly scaled. Portrait lands on the master's own
 * coordinates; landscape (no master supplied) keeps the master's ingredients and sets MADE IN
 * on one line. A second line given means the customer set the break; otherwise a long name
 * splits itself at the most even word break once it would shrink below two thirds.
 */
export function layout(cm, text, text2) {
  const W = cm[0] * 10, H = cm[1] * 10;
  const portrait = H >= W;
  const fsM = masterFs();
  const leadRatio = M.lead / fsM;
  const gapRatio = (M.base1 - (M.crest[1] + M.crest[3])) / fsM;
  const crestW = M.crest[2], crestH = M.crest[3];
  const explicit = String(text2 || '').trim().length > 0;
  const fit = (str, cap, meas) => { const at = measure(str); return at ? Math.min(cap, (100 * meas) / at) : cap; };
  const lay = (cap, meas) => {
    if (explicit) {
      const ls = [String(text || '').trim(), text2.trim()].filter((x) => x.length);
      return { lines: ls, fs: Math.min.apply(null, ls.map((l) => fit(l, cap, meas))) };
    }
    const toks = tokens(text);
    let lines = [join(toks)], fs = fit(lines[0], cap, meas);
    if (toks.length > 1 && fs < cap * 0.66) {
      let at = 1, best = Infinity;
      for (let i = 1; i < toks.length; i++) {
        const d = Math.abs(measure(join(toks.slice(0, i))) - measure(join(toks.slice(i))));
        if (d < best) { best = d; at = i; }
      }
      const two = [join(toks.slice(0, at)), join(toks.slice(at))];
      const twoFs = Math.min(fit(two[0], cap, meas), fit(two[1], cap, meas));
      if (twoFs > fs) { lines = two; fs = twoFs; }
    }
    return { lines, fs };
  };

  if (portrait) {
    const k = W / M.w, ox = 0, oy = (H - M.h * k) / 2;
    const meas = M.w * 0.82;
    const { lines, fs } = lay(fsM, meas);
    // a second town line is absorbed by lifting the block half a line, so the lockup stays
    // optically centred under the crest instead of running off the bottom
    const shift = (lines.length - 1) * M.lead * 0.5;
    const rows = [{ s: 'MADE', y: M.base1 - shift, fs: fsM }, { s: 'IN', y: M.base1 + M.lead - shift, fs: fsM }];
    lines.forEach((ln, i) => rows.push({ s: ln, y: M.base1 + M.lead * (2 + i) - shift, fs }));
    return {
      W, H, portrait, fsM, fs, measure: meas, min: W * 0.075, tight: fs < fsM * 0.5,
      crestT: 'translate(' + ox.toFixed(2) + ' ' + oy.toFixed(2) + ') scale(' + k.toFixed(5) + ')',
      rows: rows.map((r) => ({ s: r.s, x: ox + M.cx * k, y: oy + r.y * k, fs: r.fs * k })),
    };
  }

  // landscape: the crest at 0.17 of the width, letters the same physical size as portrait large
  const kc = (W * 0.17) / crestW;
  const meas = W * 0.82;
  const cap = H * 0.164;
  const { lines, fs } = lay(cap, meas);
  let madeFs = Math.min(cap, fit('MADE IN', cap, meas)), fsT = fs;
  const avail = H * 0.86 - crestH * kc;
  let typeH = gapRatio * madeFs + leadRatio * fsT * lines.length + fsT * 0.26;
  if (typeH > avail) { const r = avail / typeH; madeFs *= r; fsT *= r; typeH = avail; }
  const lead = leadRatio * fsT, gap = gapRatio * madeFs;
  const top = (H - (crestH * kc + typeH)) / 2;
  const first = top + crestH * kc + gap;
  const rows = [{ s: 'MADE IN', x: W / 2, y: first, fs: madeFs }];
  lines.forEach((ln, i) => rows.push({ s: ln, x: W / 2, y: first + lead * (i + 1), fs: fsT }));
  return {
    W, H, portrait, fsM: cap, fs: fsT, measure: meas, min: W * 0.05, tight: fsT < cap * 0.5,
    crestT: 'translate(' + (W / 2 - (M.crest[0] + crestW / 2) * kc).toFixed(2) + ' ' + (top - M.crest[1] * kc).toFixed(2) + ') scale(' + kc.toFixed(5) + ')',
    rows,
  };
}

const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// The ground, the crest and the lockup inside an <svg> tag the caller supplies
function faceBody(L, colour, hex, ground) {
  const key = colourKey(colour) || 'brown';
  const ink = PALE.test(key) ? DARK_INK : INK;
  return ground(hex || COLOURS[key] || COLOURS.brown) +
    '<g fill="' + ink + '" transform="' + L.crestT + '">' + CREST + '</g>' +
    L.rows.map((r) => '<text x="' + r.x.toFixed(2) + '" y="' + r.y.toFixed(2) + '" text-anchor="middle" style="font-family:' + FACE.replace(/"/g, '&quot;') + ';font-weight:600;font-size:' + r.fs.toFixed(2) + 'px;fill:' + ink + '">' + esc(r.s) + '</text>').join('') +
    '</svg>';
}

/** One board face as SVG markup: the ground with its own corner radius, the crest, the lockup. */
export function face(cm, text, text2, colour, hex) {
  const L = layout(cm, text, text2);
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + L.W + ' ' + L.H + '" preserveAspectRatio="none" style="display:block;width:100%;height:100%">' +
    faceBody(L, colour, hex, (fill) => '<rect x="0" y="0" width="' + L.W + '" height="' + L.H + '" rx="' + M.radius + '" ry="' + M.radius + '" fill="' + fill + '"></rect>');
}

/**
 * The print file's face (the studio print page): the same board laid out exactly as the customer
 * saw it, with the ground run square out into `bleed` mm on every edge (the corners are rounded
 * when the board is cut). Sized in millimetres; svgPrintJpeg sets the pixels.
 */
export function printFace(cm, text, text2, colour, hex, bleed = 5) {
  const L = layout(cm, text, text2);
  const box = [-bleed, -bleed, L.W + bleed * 2, L.H + bleed * 2];
  return '<svg xmlns="http://www.w3.org/2000/svg" width="' + box[2] + '" height="' + box[3] + '" viewBox="' + box.join(' ') + '">' +
    faceBody(L, colour, hex, (fill) => '<rect x="' + box[0] + '" y="' + box[1] + '" width="' + box[2] + '" height="' + box[3] + '" fill="' + fill + '"></rect>');
}

/**
 * Whether this computer has Gill Sans, the product's face. It is not licensed into the site, so a
 * print file is only right where the face is installed (the studio's Mac): elsewhere the fallback
 * would be printed.
 */
export function faceInstalled() {
  const probe = document.createElement('span');
  probe.setAttribute('style', 'position:absolute;left:-9999px;top:0;visibility:hidden;white-space:pre;font-size:100px;font-weight:600;');
  probe.textContent = M.ref;
  document.body.appendChild(probe);
  const width = (family) => ((probe.style.fontFamily = family), probe.offsetWidth);
  const base = width('monospace');
  const found = ['"Gill Sans"', '"Gill Sans MT"', '"Gill Sans Nova"'].some((f) => width(f + ', monospace') !== base);
  probe.remove();
  return found;
}

export const boardCm = (orient, size) => CM[orientKey(orient)][sizeKey(size)];

let assetMap = null;
function assets() {
  if (assetMap) return assetMap;
  const el = document.querySelector('script[data-btf-planner-assets]');
  try { assetMap = el ? JSON.parse(el.textContent) : {}; } catch (e) { assetMap = {}; }
  return assetMap;
}

/**
 * One board.
 * data-orient  Landscape | Portrait     data-size  Small | Medium | Large (or the store's option value)
 * data-view    product | room           data-cap   white | black       data-colour  colourway key
 * data-hex     ground override          data-text / data-text2         data-layer  full (default) | face
 * data-max     cap the stage width (px); omit to fill the container
 */
class TownBoard extends HTMLElement {
  static get observedAttributes() {
    return ['data-orient', 'data-size', 'data-view', 'data-cap', 'data-colour', 'data-hex', 'data-text', 'data-text2', 'data-layer', 'data-max'];
  }
  #raf = 0;
  #built = false;
  #stage; #art; #area; #caps;
  #want = '';

  connectedCallback() { this.#build(); this.#schedule(); }
  attributeChangedCallback() { if (this.#built) this.#schedule(); }

  #build() {
    if (this.#built) return;
    this.#built = true;
    this.innerHTML = '<span class="btf-pb__stage"><span class="btf-pb__art"></span><span class="btf-pb__area"></span><span class="btf-pb__caps"></span></span>';
    this.#stage = this.firstElementChild;
    [this.#art, this.#area, this.#caps] = Array.from(this.#stage.children);
  }

  #schedule() {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => { this.#raf = 0; this.#render(); });
  }

  #render() {
    const orient = orientKey(this.dataset.orient);
    const size = sizeKey(this.dataset.size);
    const room = this.dataset.view === 'room';
    const cap = /black/i.test(this.dataset.cap || '') ? 'black' : 'white';
    const cm = CM[orient][size];
    const radius = (350 / cm[0]).toFixed(2) + '% / ' + (350 / cm[1]).toFixed(2) + '%';
    const faceOnly = this.dataset.layer === 'face';

    if (faceOnly) {
      // The face alone, at the board's own proportions and corner radius (the colourway
      // swatches). The radius goes on the host as well as the stage, so the design's
      // selected outline follows the board's corners instead of squaring them off.
      this.style.cssText = 'display:block;width:100%;border-radius:' + radius + ';';
      this.#stage.style.cssText = 'display:block;position:relative;width:100%;aspect-ratio:' + cm[0] + ' / ' + cm[1] + ';overflow:hidden;border-radius:' + radius + ';';
      this.#art.style.cssText = 'display:block;position:absolute;inset:0;';
      this.#area.style.cssText = this.#caps.style.cssText = 'display:none;';
    } else {
      const pr = PROD[orient];
      const r = room ? ROOM[orient][size] : pr[size];
      const plate = (orient === 'Landscape' ? 'land' : 'port') + '-' + PLATE[size];
      const map = assets();
      const base = map[(room ? 'room-' : 'product-') + plate + (room ? '-room' : '-area')] || '';
      const caps = map[(room ? 'room-' : 'product-') + plate + '-caps-' + cap] || '';
      const max = parseInt(this.dataset.max || '', 10);
      const width = max ? 'min(100%, ' + Math.round(max * (room ? 1 : pr.arNum)) + 'px)' : '100%';
      this.#stage.style.cssText = 'display:block;position:relative;width:' + width + ';height:auto;aspect-ratio:' + (room ? '1 / 1' : pr.ar) + ';background:#fff;isolation:isolate;margin:0 auto;';
      this.#art.style.cssText = 'display:block;position:absolute;left:' + r[0] + '%;top:' + r[1] + '%;width:' + r[2] + '%;height:' + (r[3] + (room ? 0 : 0.5)) + '%;z-index:1;overflow:hidden;border-radius:' + radius + ';';
      this.#area.style.cssText = 'display:block;position:absolute;inset:0;z-index:2;pointer-events:none;mix-blend-mode:multiply;background-image:url("' + base + '");background-size:100% 100%;background-repeat:no-repeat;';
      this.#caps.style.cssText = 'display:block;position:absolute;inset:0;z-index:3;pointer-events:none;background-image:url("' + caps + '");background-size:100% 100%;background-repeat:no-repeat;';
    }

    const want = [orient, size, this.dataset.colour, this.dataset.hex, this.dataset.text, this.dataset.text2].join('\u0001');
    if (want === this.#want) return;
    this.#want = want;
    this.#art.innerHTML = face(cm, this.dataset.text || '', this.dataset.text2 || '', this.dataset.colour, this.dataset.hex);
    this.dataset.ready = 'true';
  }
}

const FILTER = /[^A-Z' \-&]/g;
const STORE_KEY = 'btf-town-state';
const SAMPLE = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * The design params a page was opened with (a shared link, assets/btf-share.js), or null when
 * there are none or a basket line is being edited, which btf-edit-line.js restores instead.
 */
function sharedDesign(keys) {
  const p = new URLSearchParams(window.location.search);
  if (p.has('edit') || (window.btfEditLine && window.btfEditLine.active)) return null;
  return keys.some((k) => p.has(k)) ? p : null;
}

/**
 * The product-page controller. Lives in the preview block and reaches the fields, the toggles
 * and Horizon's variant picker through the section, so Horizon's price and buy buttons keep
 * doing their jobs while the board follows along. Colourways are sibling products rather than
 * a variant option, so a swatch is a link: it stores the customer's words, format and caps in
 * sessionStorage and the sibling page restores them (the size travels in the link's ?variant=).
 */
class TownPreview extends HTMLElement {
  #ready = false;
  #landAt = null;
  #state = { orient: 'Portrait', cap: 'white', view: 'product' };

  connectedCallback() {
    if (this.#ready) return;
    this.#ready = true;
    this.section = this.closest('.shopify-section') || document.body;
    this.board = this.querySelector('btf-town-board');
    this.variants = this.#json(this.section.querySelector('script[data-btf-variants]')) || { names: [], list: [] };
    this.picker = this.section.querySelector('variant-picker');
    this.townInput = this.section.querySelector('[data-btf-town]');
    this.lineInput = this.section.querySelector('[data-btf-line2]');
    const orientRadio = this.section.querySelector('[data-btf-orient]:checked');
    const capRadio = this.section.querySelector('[data-btf-cap]:checked');
    this.#state.orient = orientKey(orientRadio ? orientRadio.value : this.board && this.board.dataset.orient);
    this.#state.cap = /black/i.test((capRadio ? capRadio.value : (this.board && this.board.dataset.cap)) || '')
      ? 'black'
      : 'white';

    this.section.addEventListener('input', (e) => {
      if (e.target === this.townInput || e.target === this.lineInput) this.#text();
    });
    // Format and screw caps are line-item properties on the product form, not variant options,
    // so they are ordinary radios: the form carries them and the board follows the change.
    this.section.addEventListener('change', (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.matches('[data-btf-orient]')) this.#setOrient(t.value);
      else if (t.matches('[data-btf-cap]')) this.#setCap(t.value);
      else if (this.picker && this.picker.contains(t)) this.#variant();
    });
    this.section.addEventListener('click', (e) => {
      const t = e.target.closest && e.target.closest('[data-btf-view], [data-btf-swatch]');
      if (!t || !this.section.contains(t)) return;
      if (t.hasAttribute('data-btf-swatch')) {
        this.#store(t);
      } else if (t.dataset.btfView) {
        e.preventDefault();
        this.#setView(t.dataset.btfView);
      }
    });
    if (this.townInput) this.townInput.addEventListener('focus', () => this.townInput.select());

    this.#restore();
    // A shared link (assets/btf-share.js) carries the variant, which Shopify selects, and the
    // words, format and caps, which override anything restored above
    const shared = sharedDesign(['town', 'line2', 'orient', 'caps']);
    if (shared) {
      if (this.townInput && shared.has('town')) this.townInput.value = shared.get('town');
      if (this.lineInput && shared.has('line2')) this.lineInput.value = shared.get('line2');
      if (shared.has('orient')) this.#state.orient = orientKey(shared.get('orient'));
      if (shared.has('caps')) this.#state.cap = shared.get('caps') === 'black' ? 'black' : 'white';
    }
    this.#setOrient(this.#state.orient, true);
    this.#setCap(this.#state.cap);
    this.#setView(this.#state.view);
    this.#variant();
    this.#share(Boolean(shared));
    if (this.#landAt !== null) this.#land(this.#landAt);
  }

  // "Send it to someone before you decide": shown once the design is the customer's own
  #share(open) {
    const share = this.querySelector('btf-share');
    if (!share) return;
    share.provider = {
      params: () => {
        const p = new URLSearchParams();
        if (this.variantId) p.set('variant', String(this.variantId));
        if (this.townInput && this.townInput.value) p.set('town', this.townInput.value);
        if (this.lineInput && this.lineInput.value) p.set('line2', this.lineInput.value);
        p.set('orient', this.#state.orient.toLowerCase());
        p.set('caps', this.#state.cap);
        return p;
      },
      tokens: () => {
        const w = this.townInput ? this.townInput.value : '';
        return {
          title: this.dataset.title || '',
          words: w ? ', “' + w + '”' : '',
          size: this.#cm().join(' × ') + ' cm · ' + this.#state.orient.toLowerCase(),
          price: this.variantPrice || '',
        };
      },
    };
    const show = () => (share.hidden = false);
    if (open) show();
    else {
      this.section.addEventListener('input', (e) => e.isTrusted && show());
      this.section.addEventListener('change', (e) => e.isTrusted && show());
      this.section.addEventListener('click', (e) => e.isTrusted && e.target instanceof Element && e.target.closest('[data-btf-swatch]') && show());
    }
  }

  #json(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // the words and the two property choices travel with the customer between colourway siblings
  #store(swatch) {
    // where the colour row sits on screen, so the sibling page can open with it in the same place
    const row = swatch && (swatch.closest('.btf-picker__group--colourways') || swatch);
    try {
      sessionStorage.setItem(
        STORE_KEY,
        JSON.stringify({
          y: row ? Math.round(row.getBoundingClientRect().top) : null,
          text: this.townInput ? this.townInput.value : '',
          text2: this.lineInput ? this.lineInput.value : '',
          orient: this.#state.orient,
          cap: this.#state.cap,
          view: this.#state.view,
          t: Date.now(),
        })
      );
    } catch (e) {
      /* storage unavailable: the sibling simply starts from its own defaults */
    }
  }

  #restore() {
    let s = null;
    try {
      s = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
      sessionStorage.removeItem(STORE_KEY);
    } catch (e) {
      s = null;
    }
    if (!s || Date.now() - (s.t || 0) > 600000) return;
    if (this.townInput && typeof s.text === 'string') this.townInput.value = s.text;
    if (this.lineInput && typeof s.text2 === 'string') this.lineInput.value = s.text2;
    if (s.orient) this.#state.orient = orientKey(s.orient);
    if (s.cap) this.#state.cap = s.cap === 'black' ? 'black' : 'white';
    if (s.view) this.#state.view = s.view === 'room' ? 'room' : 'product';
    if (typeof s.y === 'number') this.#landAt = s.y;
  }

  // A colourway is its own page, which a browser opens at the top. Opened from a swatch, this
  // page scrolls so its colour row sits where the customer clicked it, and settles again once
  // the pictures above have loaded, unless the customer has started scrolling by then.
  #land(y) {
    const row = this.section.querySelector('.btf-picker__group--colourways');
    if (!row) return;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    let moved = false;
    const stop = () => (moved = true);
    ['wheel', 'touchstart', 'keydown'].forEach((ev) => window.addEventListener(ev, stop, { once: true, passive: true }));
    // Horizon scrolls the page inside .page-wrapper on desktop, and the window on a phone
    const go = () => {
      if (moved) return;
      const wrap = document.querySelector('.page-wrapper');
      const inner = wrap && /auto|scroll/.test(getComputedStyle(wrap).overflowY) && wrap.scrollHeight > wrap.clientHeight;
      const by = row.getBoundingClientRect().top - y;
      if (inner) wrap.scrollTo({ top: wrap.scrollTop + by, behavior: 'instant' });
      else window.scrollTo({ top: window.scrollY + by, behavior: 'instant' });
    };
    go();
    requestAnimationFrame(go);
    if (document.readyState === 'complete') setTimeout(go, 0);
    else window.addEventListener('load', go, { once: true });
  }

  #selected() {
    const out = {};
    if (!this.picker) return out;
    this.picker.querySelectorAll('input[type="radio"]:checked').forEach((i) => {
      if (i.dataset.optionName) out[i.dataset.optionName] = i.value;
    });
    this.picker.querySelectorAll('select').forEach((s) => {
      const o = s.selectedOptions[0];
      if (o && o.dataset.optionName) out[o.dataset.optionName] = o.value;
    });
    return out;
  }

  #sizeValue() {
    const sel = this.#selected();
    const k = Object.keys(sel).find((key) => /size/i.test(key));
    return k ? sel[k] : '';
  }

  #cm() {
    return CM[this.#state.orient][sizeKey(this.#sizeValue())];
  }

  #boards() {
    return Array.from(this.section.querySelectorAll('btf-town-board'));
  }

  #text() {
    const cm = this.#cm();
    // The limits are what the lockup will actually carry at the smallest size the design
    // allows, measured in the rendered face: two lines' worth for the first box (it still
    // feeds the automatic split) and one line's worth for the explicit second line.
    const L = layout(cm, 'A', '');
    const perChar = (measure(SAMPLE) / SAMPLE.length) * ((L.fsM * 0.5) / 100);
    const max = perChar ? Math.max(12, Math.floor((L.measure * 2) / perChar)) : 24;
    const max2 = perChar ? Math.max(10, Math.floor(L.measure / perChar)) : 20;
    const clean = (input, cap) => {
      if (!input) return '';
      const v = input.value.toUpperCase().replace(FILTER, '').slice(0, cap);
      if (v !== input.value) input.value = v;
      input.setAttribute('maxlength', cap);
      return v;
    };
    const text = clean(this.townInput, max);
    const text2 = clean(this.lineInput, max2);
    this.#count('town', text.length, max);
    this.#count('line2', text2.length, max2);
    const T = layout(cm, text, text2);
    this.section.querySelectorAll('[data-btf-tight]').forEach((el) => {
      el.hidden = !T.tight;
    });
    this.#boards().forEach((b) => {
      b.dataset.text = text;
      b.dataset.text2 = text2;
    });
  }

  #count(key, n, max) {
    this.section.querySelectorAll('[data-btf-count="' + key + '"]').forEach((el) => {
      el.textContent = n + ' / ' + max;
    });
  }

  // The picker opened with no size chosen (blocks/btf-product-picker.liquid) and none is ticked
  // yet: the preview stays on the large board and the price reads "From £75"
  #sizeOpen() {
    const g = this.section.querySelector('.btf-picker__group--size');
    return !!(g && g.querySelector('[data-btf-size-prompt]') && !g.querySelector('input:checked'));
  }

  #variant() {
    const sel = this.#selected();
    const names = this.variants.names || [];
    const list = this.variants.list || [];
    const variant = list.find((v) => names.every((n, i) => sel[n] == null || v.options[i] === sel[n])) || list[0];
    this.variantId = variant ? variant.id : null;
    this.variantPrice = variant ? variant.price_formatted : '';
    const size = sizeKey(this.#sizeValue());
    const orient = this.#state.orient;
    const cm = CM[orient][size];
    this.#boards().forEach((b) => {
      b.dataset.size = size;
    });
    const label = cm[0] + ' × ' + cm[1] + ' cm · ' + orient.toLowerCase();
    this.section.querySelectorAll('[data-btf-size-label]').forEach((el) => (el.textContent = label));
    const open = this.#sizeOpen();
    const price = open ? this.variants.from || '' : variant ? variant.price_formatted : '';
    this.section.querySelectorAll('[data-btf-price]').forEach((el) => (el.textContent = price));
    // "Add to basket — £145": the label carries the price, as drawn
    this.section
      .querySelectorAll('[data-btf-basket-price]')
      .forEach((el) => (el.textContent = !open && variant && variant.available ? ' — ' + price : ''));
    // The colourway links carry the chosen size in their own ?variant=; Horizon re-renders
    // this section on every variant change, so Liquid re-points them for us.
    this.#text();
  }

  // a radio group restored from a sibling page: check the one that matches
  #check(selector, test) {
    this.section.querySelectorAll(selector).forEach((i) => {
      if (test(i.value) && !i.checked) i.checked = true;
    });
  }

  #setOrient(value, silent) {
    this.#state.orient = orientKey(value);
    this.#boards().forEach((b) => {
      b.dataset.orient = this.#state.orient;
    });
    this.#check('[data-btf-orient]', (v) => orientKey(v) === this.#state.orient);
    if (!silent) this.#variant();
  }

  #setCap(value) {
    this.#state.cap = /black/i.test(value) ? 'black' : 'white';
    this.#boards().forEach((b) => {
      b.dataset.cap = this.#state.cap;
    });
    this.#check('[data-btf-cap]', (v) => (/black/i.test(v) ? 'black' : 'white') === this.#state.cap);
  }

  #setView(view) {
    this.#state.view = view === 'room' ? 'room' : 'product';
    if (this.board) this.board.dataset.view = this.#state.view;
    this.section.querySelectorAll('[data-btf-view]').forEach((tab) => {
      const on = tab.dataset.btfView === this.#state.view;
      tab.setAttribute('aria-pressed', on ? 'true' : 'false');
      tab.classList.toggle('is-on', on);
    });
  }
}

if (!customElements.get('btf-town-board')) customElements.define('btf-town-board', TownBoard);
if (!customElements.get('btf-town-preview')) customElements.define('btf-town-preview', TownPreview);
